'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, BookOpen, Search, FileText, HelpCircle } from 'lucide-react';
import { VirzaMark } from '@/components/branding/virza-mark';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] as const },
  }),
};

const quickLinks = [
  { href: '/documents', icon: FileText, label: 'Documents', desc: 'Your research library' },
  { href: '/search', icon: Search, label: 'Search', desc: 'Find papers & notes' },
  { href: '/notes', icon: BookOpen, label: 'Notes', desc: 'Your research notes' },
  {
    href: 'https://virza.io/support',
    icon: HelpCircle,
    label: 'Support',
    desc: 'Get help from our team',
    external: true,
  },
] as const;

export default function NotFound() {
  return (
    <div className="bg-background flex min-h-dvh items-center justify-center px-6 py-28 sm:py-36">
      <div className="mx-auto w-full max-w-[640px] text-center">
        {/* ── Virza Logo ─────────────────────────────────────── */}
        <motion.div
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="mb-12 flex justify-center"
        >
          <Link href="/" className="group inline-flex items-center gap-3.5">
            <div className="transition-transform duration-300 group-hover:scale-110">
              <VirzaMark variant="auto" size={36} />
            </div>
            <span className="text-foreground text-2xl font-semibold tracking-[-0.025em]">
              Virza
            </span>
          </Link>
        </motion.div>

        {/* ── Large 404 ──────────────────────────────────────── */}
        <motion.div
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="mb-6 select-none"
        >
          <span className="text-muted-foreground/20 block text-[8rem] leading-none font-extrabold tracking-[-0.06em] sm:text-[11rem] md:text-[14rem]">
            404
          </span>
        </motion.div>

        {/* ── Heading ────────────────────────────────────────── */}
        <motion.h1
          custom={2}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="text-foreground mb-4 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl"
        >
          Page not found
        </motion.h1>

        <motion.p
          custom={3}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="text-muted-foreground mx-auto mb-10 max-w-[420px] text-base leading-relaxed sm:text-lg"
        >
          The page you&apos;re looking for doesn&apos;t exist or has been moved. Let&apos;s get you
          back on track.
        </motion.p>

        {/* ── Action Buttons ─────────────────────────────────── */}
        <motion.div
          custom={4}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="mb-16 flex flex-wrap justify-center gap-3"
        >
          <Link
            href="/home"
            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-12 items-center gap-2.5 rounded-xl px-8 text-[15px] font-semibold transition-all duration-200 active:scale-[0.97]"
          >
            <Home className="h-4 w-4" />
            Go to Home
          </Link>
          <button
            onClick={() => window.history.back()}
            className="border-border bg-card text-foreground hover:bg-accent inline-flex h-12 items-center gap-2.5 rounded-xl border px-8 text-[15px] font-semibold transition-all duration-200 active:scale-[0.97]"
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </button>
        </motion.div>

        {/* ── Quick Links Card ───────────────────────────────── */}
        <motion.div
          custom={5}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="border-border bg-card rounded-2xl border p-7 sm:p-8"
        >
          <h2 className="text-muted-foreground mb-5 text-[11px] font-bold tracking-[0.06em] uppercase">
            Looking for something?
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {quickLinks.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                {...('external' in item ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="border-border hover:bg-accent group flex items-center gap-4 rounded-xl border p-4 text-left transition-all duration-200 hover:-translate-y-0.5"
              >
                <div className="bg-muted flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl">
                  <item.icon className="text-muted-foreground h-[18px] w-[18px]" />
                </div>
                <div className="min-w-0">
                  <p className="text-foreground text-sm font-medium">{item.label}</p>
                  <p className="text-muted-foreground mt-0.5 text-xs">{item.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>

        {/* ── Footer branding ────────────────────────────────── */}
        <motion.div
          custom={6}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="mt-14 flex items-center justify-center gap-3"
        >
          <VirzaMark variant="auto" size={16} />
          <span className="text-muted-foreground/40 text-sm tracking-[-0.01em]">
            Research without the chaos
          </span>
        </motion.div>
      </div>
    </div>
  );
}
